/* Product dependency warnings
 *
 * Adds warnings to:
 * - Selleasy add-on cards on product pages (`.lb-widget-pal`)
 * - cart line items (`.cart-item[data-product-handle]`), including the drawer
 *
 * Rules come from two JSON script tags rendered by the theme:
 * - #product-dependency-rules        (snippets/product-dependency-rules-json.liquid)
 * - #product-dependency-rules-legacy (snippets/product-dependency-rules.liquid)
 *
 * The JSON config wins when both define the same product handle.
 */
(function () {
  'use strict';

  var WIDGET_SELECTOR = '.lb-widget-pal';
  var JSON_RULES_ID = 'product-dependency-rules';
  var LEGACY_RULES_ID = 'product-dependency-rules-legacy';
  var WIDGET_WARNING_CLASS = 'selleasy-fbt-warning';
  var CART_WARNING_CLASS = 'cart-item__dependency';
  var CART_WARNING_CLASSES =
    'cart-item__dependency text-sm mt-2 text-error-text';

  var scheduled = false;

  function readRules(id) {
    var el = document.getElementById(id);
    if (!el) return {};

    try {
      return JSON.parse(el.textContent) || {};
    } catch (error) {
      return {};
    }
  }

  function hasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  }

  function mergeRules() {
    var merged = {};
    var legacy = readRules(LEGACY_RULES_ID);
    var json = readRules(JSON_RULES_ID);
    var key;

    for (key in legacy) {
      if (hasOwn(legacy, key)) merged[key] = legacy[key];
    }
    for (key in json) {
      if (hasOwn(json, key)) merged[key] = json[key];
    }

    return merged;
  }

  function normalizeRule(rule) {
    if (!rule || !rule.requires) return null;

    var requires = rule.requires;
    if (typeof requires === 'string') requires = requires.split(',');

    var handles = [];
    for (var i = 0; i < requires.length; i++) {
      var handle = String(requires[i]).trim();
      if (handle) handles.push(handle);
    }

    if (!handles.length) return null;

    return {
      requires: handles,
      match: rule.match,
      message: rule.message || ''
    };
  }

  function isChecked(card) {
    var input = card.querySelector('input.lb-card-checkbox');
    return !!(input && input.checked);
  }

  function widgetSelection(widget) {
    var selected = {};
    var cards = widget.querySelectorAll(
      'lb-upsell-flat-card[lb-product-handle]'
    );

    for (var i = 0; i < cards.length; i++) {
      if (isChecked(cards[i])) {
        selected[cards[i].getAttribute('lb-product-handle')] = true;
      }
    }

    return selected;
  }

  function cartSelection(container) {
    var selected = {};
    var rows = container.querySelectorAll('.cart-item[data-product-handle]');

    for (var i = 0; i < rows.length; i++) {
      selected[rows[i].getAttribute('data-product-handle')] = true;
    }

    return selected;
  }

  function isUnsatisfied(selected, rule) {
    var missing = 0;

    for (var i = 0; i < rule.requires.length; i++) {
      if (!selected[rule.requires[i]]) missing++;
    }

    return rule.match === 'any'
      ? missing === rule.requires.length
      : missing > 0;
  }

  function applyWidgetWarning(card, rule, shouldShow) {
    // Attach to the price (rather than the card) so the warning can be
    // positioned directly below it without changing the card's grid rows.
    var host = card.querySelector('lb-price') || card.querySelector('.lb-flat-card');
    if (!host) return;

    var warning = host.querySelector('.' + WIDGET_WARNING_CLASS);
    if (!warning) {
      warning = document.createElement('p');
      warning.className = WIDGET_WARNING_CLASS;
      warning.setAttribute('role', 'note');
      host.appendChild(warning);
    }

    if (warning.textContent !== rule.message) {
      warning.textContent = rule.message;
    }
    if (warning.hidden !== !shouldShow) warning.hidden = !shouldShow;

    var flat = card.querySelector('.lb-flat-card');
    if (!flat) return;

    if (!shouldShow) {
      flat.classList.remove('has-dependency-warning');
      flat.style.removeProperty('--dependency-warning-space');
      return;
    }

    flat.classList.add('has-dependency-warning');

    // The warning is absolutely positioned, so it can extend past the card's
    // content box when the message wraps on narrow cards. Reserve just enough
    // bottom padding to keep a 14px gap above the border.
    var warningRect = warning.getBoundingClientRect();
    var flatRect = flat.getBoundingClientRect();
    var style = window.getComputedStyle(flat);
    var paddingBottom = parseFloat(style.paddingBottom) || 0;
    var borderBottom = parseFloat(style.borderBottomWidth) || 0;
    var currentSpace =
      parseFloat(style.getPropertyValue('--dependency-warning-space')) || 0;
    var contentBottom = flatRect.bottom - paddingBottom - borderBottom;
    var basePadding = paddingBottom - currentSpace;
    var neededSpace = Math.ceil(
      warningRect.bottom + 14 - contentBottom - borderBottom - basePadding
    );
    var nextSpace = Math.max(0, neededSpace);

    if (nextSpace !== currentSpace) {
      flat.style.setProperty('--dependency-warning-space', nextSpace + 'px');
    }
  }

  function updateWidgets(rules) {
    var widgets = document.querySelectorAll(WIDGET_SELECTOR);

    for (var i = 0; i < widgets.length; i++) {
      var widget = widgets[i];

      // Wait until the Selleasy app has hydrated the widget.
      if (!widget.querySelector('lb-upsell-widget')) continue;

      var selected = widgetSelection(widget);
      var cards = widget.querySelectorAll(
        'lb-upsell-flat-card[lb-product-handle]'
      );

      for (var j = 0; j < cards.length; j++) {
        var card = cards[j];
        var rule = normalizeRule(rules[card.getAttribute('lb-product-handle')]);
        if (!rule || !rule.message) continue;

        applyWidgetWarning(
          card,
          rule,
          isChecked(card) && isUnsatisfied(selected, rule)
        );
      }
    }
  }

  function updateCarts(rules) {
    var containers = document.querySelectorAll('cart-items');

    for (var i = 0; i < containers.length; i++) {
      var container = containers[i];
      var selected = cartSelection(container);
      var rows = container.querySelectorAll('.cart-item[data-product-handle]');

      for (var j = 0; j < rows.length; j++) {
        var row = rows[j];
        var details = row.querySelector('.cart-item__details');
        if (!details) continue;

        var rule = normalizeRule(rules[row.getAttribute('data-product-handle')]);
        var warning = details.querySelector('.' + CART_WARNING_CLASS);
        var shouldShow = !!rule && !!rule.message && isUnsatisfied(selected, rule);

        if (!shouldShow) {
          if (warning) warning.remove();
          continue;
        }

        if (!warning) {
          warning = document.createElement('p');
          warning.className = CART_WARNING_CLASSES;
          warning.setAttribute('role', 'note');
          details.appendChild(warning);
        }

        if (warning.textContent !== rule.message) {
          warning.textContent = rule.message;
        }
      }
    }
  }

  function update() {
    var rules = mergeRules();
    updateWidgets(rules);
    updateCarts(rules);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      update();
    });
  }

  // The Selleasy widget and the cart are rendered asynchronously and can be
  // re-rendered (quantity changes, drawer opens), so watch the whole document.
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  // Checkbox clicks change `checked` without an attribute mutation.
  document.addEventListener('change', schedule, true);
  document.addEventListener('click', schedule, true);

  update();
})();
